import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { toast } from "sonner";
import { Mail, Lock, User, LogIn, UserPlus, Sparkles, Eye, EyeOff } from "lucide-react";
import logoImg from "@/assets/logo.png";
import { SEO } from "@/components/SEO";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(() => {
    if (typeof window === "undefined") return true;
    const visited = localStorage.getItem("savey_visited");
    if (!visited) {
      localStorage.setItem("savey_visited", "1");
      return false; // first-time visitor → show Sign Up
    }
    return true;
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupAttempted, setSignupAttempted] = useState(false);
  const [signupSent, setSignupSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  // Same-origin relative-path validator for consumption of ?next=... after auth.
  // Ensures we can return the user to an OAuth consent URL (or any protected route)
  // preserved across sign-in / sign-up without allowing open redirects.
  const nextTarget = (() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("next");
    if (!raw) return null;
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
  })();

  useEffect(() => {
    const routeAfterAuth = (userId: string) => {
      if (nextTarget) {
        window.location.replace(nextTarget);
        return;
      }
      let seen = false;
      try { seen = localStorage.getItem("onboardingSeen") === "1"; } catch { /* ignore */ }
      if (seen) {
        navigate("/app");
        return;
      }
      supabase.from("profiles").select("monthly_salary").eq("id", userId).single().then(({ data }) => {
        navigate(data?.monthly_salary ? "/app" : "/onboarding");
      });

    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) routeAfterAuth(session.user.id);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) routeAfterAuth(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, [navigate, nextTarget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back!");
      } else {
        setSignupAttempted(true);
        if (password !== confirmPassword) {
          toast.error("Passwords don't match.");
          setLoading(false);
          return;
        }
        // Block obvious placeholder / fake names
        const normalized = fullName.trim().toLowerCase().replace(/\s+/g, " ");
        const blockedNames = new Set([
          "john doe", "jane doe", "john smith", "jane smith", "joe doe",
          "test user", "test test", "test name", "fake user", "fake name",
          "dummy user", "dummy name", "user user", "first last",
          "name", "your name", "full name", "test", "asdf", "qwerty",
          "abc", "abc abc", "aaa", "xxx", "anonymous", "anon", "user",
          "admin", "demo", "demo user", "sample", "sample user",
        ]);
        const collapsed = normalized.replace(/\s/g, "");
        const looksFake =
          !normalized ||
          normalized.length < 3 ||
          !normalized.includes(" ") || // require first + last name
          blockedNames.has(normalized) ||
          /\b(john|jane)\s*do[e3]\b/.test(normalized) ||
          /^(.)\1+$/.test(collapsed) || // aaaa, xxxx
          /^(test|fake|dummy|sample|demo|asdf|qwerty)/.test(normalized) ||
          /\d/.test(normalized); // names shouldn't have digits
        if (looksFake) {
          toast.error("Please enter your real full name (first and last).");
          setLoading(false);
          return;
        }
        // Block placeholder emails
        const emailLower = email.trim().toLowerCase();
        if (/@example\.(com|org|net)$/.test(emailLower) || /^(test|dummy|fake|john\.?doe|jane\.?doe)@/.test(emailLower)) {
          toast.error("Please use a real email address.");
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        // Redeem referral code if user landed via /r/:code
        try {
          const refCode = localStorage.getItem("savey:referral_code");
          if (refCode) {
            await supabase.functions.invoke("redeem-referral", { body: { code: refCode } });
            localStorage.removeItem("savey:referral_code");
          }
        } catch { /* non-blocking */ }
        setSignupSent(true);
        toast.success("Check your email to verify your account!");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("Enter your email above first.");
      return;
    }
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      toast.success("Verification email resent. Check your inbox.");
    } catch (err: any) {
      toast.error(err.message || "Couldn't resend the email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
    <SEO
      title="Sign in or create your SaveySpend account"
      description="Log in or sign up to SaveySpend — the AI savings planner that builds personalized plans and tracks your money habits."
      path="/auth"
    />
    <main className="min-h-screen mesh-gradient flex items-center justify-center p-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 rounded-full bg-accent/8 blur-3xl" />
      </div>

      <div className="glass-panel rounded-2xl p-8 w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <img src={logoImg} alt="SaveySpend AI savings planner logo" width={64} height={64} className="h-16 w-16 mx-auto mb-3" />
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isLogin ? "Sign in to access your savings plans" : "Start your savings journey today"}
          </p>
        </div>

        <div className="mb-5">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              disabled={loading}
              aria-label="Continue with Google"
              onClick={async () => {
                try {
                  if (nextTarget) sessionStorage.setItem("savey:post_auth_next", nextTarget);
                  const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
                  if (result.error) toast.error((result.error as any)?.message || "Google sign-in failed.");
                } catch (e: any) {
                  toast.error(e?.message || "Google sign-in failed.");
                }
              }}
              className="group h-10 rounded-lg bg-background/40 hover:bg-primary/10 border border-primary/20 hover:border-primary/40 text-foreground/80 hover:text-foreground text-xs font-medium flex items-center justify-center gap-2 transition disabled:opacity-60 backdrop-blur-sm"
            >
              <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Google
            </button>
            <button
              type="button"
              disabled={loading}
              aria-label="Continue with Apple"
              onClick={async () => {
                try {
                  if (nextTarget) sessionStorage.setItem("savey:post_auth_next", nextTarget);
                  const result = await lovable.auth.signInWithOAuth("apple", { redirect_uri: window.location.origin });
                  if (result.error) toast.error((result.error as any)?.message || "Apple sign-in failed.");
                } catch (e: any) {
                  toast.error(e?.message || "Apple sign-in failed.");
                }
              }}
              className="group h-10 rounded-lg bg-background/40 hover:bg-primary/10 border border-primary/20 hover:border-primary/40 text-foreground/80 hover:text-foreground text-xs font-medium flex items-center justify-center gap-2 transition disabled:opacity-60 backdrop-blur-sm"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.365 1.43c0 1.14-.42 2.24-1.17 3.08-.8.9-2.1 1.6-3.15 1.52-.14-1.1.42-2.27 1.16-3.07.83-.9 2.24-1.55 3.16-1.53zM20.5 17.5c-.55 1.27-.82 1.83-1.53 2.95-1 1.55-2.4 3.48-4.14 3.5-1.55.01-1.95-.99-4.06-.98-2.11.01-2.55 1-4.1.98-1.74-.02-3.06-1.76-4.06-3.3C.34 16.05-.03 10.4 2.7 7.5c1.19-1.27 2.86-2.06 4.53-2.06 1.76 0 2.87.98 4.32.98 1.42 0 2.28-.98 4.32-.98 1.53 0 3.16.83 4.32 2.27-3.8 2.09-3.18 7.53.31 9.79z"/>
              </svg>
              Apple
            </button>
            <button
              type="button"
              disabled={loading}
              aria-label="Continue with Microsoft"
              onClick={async () => {
                try {
                  if (nextTarget) sessionStorage.setItem("savey:post_auth_next", nextTarget);
                  const result = await lovable.auth.signInWithOAuth("microsoft", { redirect_uri: window.location.origin });
                  if (result.error) toast.error((result.error as any)?.message || "Microsoft sign-in failed.");
                } catch (e: any) {
                  toast.error(e?.message || "Microsoft sign-in failed.");
                }
              }}
              className="group h-10 rounded-lg bg-background/40 hover:bg-primary/10 border border-primary/20 hover:border-primary/40 text-foreground/80 hover:text-foreground text-xs font-medium flex items-center justify-center gap-2 transition disabled:opacity-60 backdrop-blur-sm"
            >
              <svg width="13" height="13" viewBox="0 0 23 23" aria-hidden="true">
                <path fill="#F25022" d="M1 1h10v10H1z" />
                <path fill="#7FBA00" d="M12 1h10v10H12z" />
                <path fill="#00A4EF" d="M1 12h10v10H1z" />
                <path fill="#FFB900" d="M12 12h10v10H12z" />
              </svg>
              Microsoft
            </button>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            <span className="text-[10px] text-muted-foreground/70 uppercase tracking-[0.15em]">or with email</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <label htmlFor="auth-fullname" className="flex items-center gap-2 text-sm font-medium text-foreground">
                <User className="h-4 w-4 text-primary" /> Full Name
              </label>
              <input
                id="auth-fullname"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
                required={!isLogin}
                className="glass-input flex h-11 w-full rounded-xl px-4 py-2 text-base outline-none placeholder:text-muted-foreground/60"
              />
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="auth-email" className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Mail className="h-4 w-4 text-primary" /> Email
            </label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (signupSent) setSignupSent(false);
              }}
              placeholder="you@example.com"
              required
              className="glass-input flex h-11 w-full rounded-xl px-4 py-2 text-base outline-none placeholder:text-muted-foreground/60"
            />
            {!isLogin && signupAttempted && (
              <div className="space-y-1">
                {signupSent && (
                  <p className="text-xs text-muted-foreground">Verification e-mail sent. Check your inbox or spam folder.</p>
                )}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
                >
                  {resending ? "Resending…" : "Didn't get the e-mail? Resend."}
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="auth-password" className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Lock className="h-4 w-4 text-primary" /> Password
            </label>
            <div className="relative">
              <input
                id="auth-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="glass-input flex h-11 w-full rounded-xl px-4 py-2 pr-11 text-base outline-none placeholder:text-muted-foreground/60"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {!isLogin && password && (() => {
              const checks = [
                { ok: password.length >= 8, label: "At least 8 characters" },
                { ok: /[A-Z]/.test(password), label: "An uppercase letter" },
                { ok: /[a-z]/.test(password), label: "A lowercase letter" },
                { ok: /\d/.test(password), label: "A number" },
                { ok: /[^A-Za-z0-9]/.test(password), label: "A symbol (!@#…)" },
              ];
              const score = checks.filter(c => c.ok).length;
              const levels = [
                { label: "Very weak", color: "bg-red-500", text: "text-red-500" },
                { label: "Weak", color: "bg-red-400", text: "text-red-400" },
                { label: "Fair", color: "bg-amber-400", text: "text-amber-400" },
                { label: "Good", color: "bg-lime-400", text: "text-lime-500" },
                { label: "Strong", color: "bg-emerald-500", text: "text-emerald-500" },
                { label: "Excellent", color: "bg-emerald-500", text: "text-emerald-500" },
              ];
              const lvl = levels[score];
              return (
                <div className="space-y-2 mt-1">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${i < score ? lvl.color : "bg-muted"}`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${lvl.text}`}>Strength: {lvl.label}</p>
                  <ul className="text-xs space-y-0.5">
                    {checks.map((c) => (
                      <li key={c.label} className={c.ok ? "text-emerald-500" : "text-muted-foreground"}>
                        {c.ok ? "✓" : "○"} {c.label}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </div>

          {!isLogin && (
            <div className="space-y-2">
              <label htmlFor="auth-confirm-password" className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Lock className="h-4 w-4 text-primary" /> Confirm Password
              </label>
              <input
                id="auth-confirm-password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required={!isLogin}
                minLength={6}
                className="glass-input flex h-11 w-full rounded-xl px-4 py-2 text-base outline-none placeholder:text-muted-foreground/60"
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="text-xs text-destructive">Passwords don't match.</p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="glass-button w-full h-12 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
          >
            {loading ? (
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : isLogin ? (
              <>
                <LogIn className="h-4 w-4" /> Sign In
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" /> Sign Up
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-primary hover:underline"
          >
            {isLogin ? "Don't have an account? Sign up for free" : "Already have an account? Sign in"}
          </button>
        </div>

        <div className="mt-6 pt-5 border-t border-border/40 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <Link to="/pricing" className="hover:text-foreground hover:underline">Pricing</Link>
          <Link to="/terms" className="hover:text-foreground hover:underline">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground hover:underline">Privacy</Link>
          <Link to="/refund-policy" className="hover:text-foreground hover:underline">Refunds</Link>
        </div>
      </div>
    </main>
    </>
  );
};

export default Auth;
